import { createFileRoute } from "@tanstack/react-router";
import { productsInfiniteQuery } from "@/api/queries/products";
import type { CategorySlug } from "@/api/types";
import { MarketplacePage } from "@/pages/marketplace/MarketplacePage";
import { CATEGORY_SLUGS } from "@/pages/marketplace/categories";
import { findTaxonomyNode } from "@/lib/marketplace-taxonomy";
import { isGameTypeSlug } from "@/lib/game-types";

/**
 * Slugs that were renamed when the taxonomy was regrouped. Links and bookmarks
 * from before the rename still resolve rather than silently dropping to "all
 * assets", which is what an unrecognised category does.
 */
const RENAMED_CATEGORIES: Record<string, CategorySlug> = {
  models: "3d",
  "ui-kits": "ui",
};

function parseCategory(value: unknown): CategorySlug | undefined {
  if (typeof value !== "string") return undefined;
  if (CATEGORY_SLUGS.includes(value as CategorySlug)) return value as CategorySlug;
  return RENAMED_CATEGORIES[value];
}

const PRICES = ["all", "free", "paid", "sale"] as const;
const RATINGS = [0, 3, 4] as const;
const SORTS = ["popular", "trending", "newest", "price-asc", "price-desc", "rating"] as const;
const PUBLISHED_WITHIN = [7, 30, 90] as const;

type MarketplaceSearch = {
  q: string;
  category?: CategorySlug;
  subcategory?: string;
  subscope?: string;
  gameType?: string;
  publishedWithin?: (typeof PUBLISHED_WITHIN)[number];
  price: (typeof PRICES)[number];
  minRating: (typeof RATINGS)[number];
  sort: (typeof SORTS)[number];
};

function parseSearch(search: Record<string, unknown>): MarketplaceSearch {
  const q = typeof search.q === "string" ? search.q : "";
  const category = parseCategory(search.category);
  const price =
    typeof search.price === "string" && PRICES.includes(search.price as MarketplaceSearch["price"])
      ? (search.price as MarketplaceSearch["price"])
      : "all";
  const ratingValue =
    typeof search.minRating === "number" ? search.minRating : Number(search.minRating);
  const minRating = RATINGS.includes(ratingValue as MarketplaceSearch["minRating"])
    ? (ratingValue as MarketplaceSearch["minRating"])
    : 0;
  const sort =
    typeof search.sort === "string" && SORTS.includes(search.sort as MarketplaceSearch["sort"])
      ? (search.sort as MarketplaceSearch["sort"])
      : "popular";
  const subcategory =
    category && typeof search.subcategory === "string" && findTaxonomyNode(category, search.subcategory)
      ? search.subcategory
      : undefined;
  const subscope =
    category && subcategory && typeof search.subscope === "string"
      ? findTaxonomyNode(category, subcategory)?.children?.some((node) => node.slug === search.subscope)
        ? search.subscope
        : undefined
      : undefined;
  const gameType =
    typeof search.gameType === "string" && isGameTypeSlug(search.gameType) ? search.gameType : undefined;
  const publishedWithinValue =
    typeof search.publishedWithin === "number" ? search.publishedWithin : Number(search.publishedWithin);
  const publishedWithin = PUBLISHED_WITHIN.includes(publishedWithinValue as (typeof PUBLISHED_WITHIN)[number])
    ? (publishedWithinValue as (typeof PUBLISHED_WITHIN)[number])
    : undefined;
  return { q, category, subcategory, subscope, gameType, publishedWithin, price, minRating, sort };
}

export const Route = createFileRoute("/_app/marketplace")({
  validateSearch: parseSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    context.queryClient.prefetchInfiniteQuery(
      productsInfiniteQuery({
        ...deps,
        price: deps.price === "all" ? undefined : deps.price,
      }),
    ),
  component: MarketplacePage,
});
